<div class="max-w-7xl mx-auto p-6">
  <!-- En-tête -->
  <div class="bg-white shadow rounded-lg p-6 mb-8">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 mb-2">
          <i class="pi pi-bell mr-3 text-blue-500"></i>
          Gestion des Notifications
        </h1>
        <p class="text-gray-600">Créez et gérez les notifications pour tous les utilisateurs</p>
      </div>
      
      <div class="flex gap-3">
        <p-button 
          icon="pi pi-refresh" 
          (onClick)="refreshNotifications()"
          [loading]="isLoading()"
          styleClass="p-button-outlined"
          pTooltip="Actualiser">
        </p-button>
        
        <p-button 
          icon="pi pi-plus" 
          label="Nouvelle notification"
          (onClick)="openCreateDialog()"
          styleClass="p-button-primary">
        </p-button>
      </div>
    </div>
  </div>

  <!-- Statistiques -->
  <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
            <i class="pi pi-list text-white"></i>
          </div>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-gray-500">Total</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats().total }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
            <i class="pi pi-check-circle text-white"></i>
          </div>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-gray-500">Actives</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats().active }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
            <i class="pi pi-times-circle text-white"></i>
          </div>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-gray-500">Inactives</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats().inactive }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
            <i class="pi pi-eye text-white"></i>
          </div>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-gray-500">Lectures totales</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats().totalReads }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Table des notifications -->
  <div class="bg-white shadow rounded-lg overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200">
      <h2 class="text-lg font-semibold text-gray-900">
        Notifications ({{ notifications().length }})
      </h2>
    </div>

    <p-table 
      [value]="notifications()" 
      [loading]="isLoading()"
      responsiveLayout="scroll"
      [paginator]="true"
      [rows]="10"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} notifications"
      [rowHover]="true">
      
      <ng-template pTemplate="header">
        <tr>
          <th>Notification</th>
          <th>Type</th>
          <th>Statut</th>
          <th>Statistiques</th>
          <th>Créé le</th>
          <th>Expire le</th>
          <th>Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-notification>
        <tr>
          <!-- Notification -->
          <td>
            <div class="flex items-start">
              <div class="flex-shrink-0 mr-3">
                <div class="w-10 h-10 rounded-full flex items-center justify-center"
                     [ngClass]="'bg-' + notification.icon_color + '-100'">
                  <i [class]="'pi ' + notification.icon + ' text-' + notification.icon_color + '-600'"></i>
                </div>
              </div>
              <div>
                <div class="font-medium text-gray-900">{{ notification.title }}</div>
                <div class="text-sm text-gray-500 max-w-xs truncate">{{ notification.message }}</div>
                <div class="text-xs text-gray-400 mt-1">
                  Par {{ notification.created_by_name }}
                </div>
              </div>
            </div>
          </td>

          <!-- Type -->
          <td>
            <p-tag 
              [value]="getNotificationTypeLabel(notification.notification_type)"
              [severity]="getNotificationTypeSeverity(notification.notification_type)">
            </p-tag>
          </td>

          <!-- Statut -->
          <td>
            <p-tag 
              [value]="notification.is_active ? 'Active' : 'Inactive'"
              [severity]="notification.is_active ? 'success' : 'danger'">
            </p-tag>
          </td>

          <!-- Statistiques -->
          <td>
            <div class="text-sm">
              <div class="flex items-center mb-1">
                <span class="text-gray-600 mr-2">Notifiés:</span>
                <span class="font-medium">{{ notification.total_users_notified || 0 }}</span>
              </div>
              <div class="flex items-center mb-1">
                <span class="text-gray-600 mr-2">Lues:</span>
                <span class="font-medium">{{ notification.users_read_count || 0 }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-blue-600 h-2 rounded-full" 
                     [style.width.%]="getReadPercentage(notification)"></div>
              </div>
              <div class="text-xs text-gray-500 mt-1">
                {{ getReadPercentage(notification) }}% de lecture
              </div>
            </div>
          </td>

          <!-- Créé le -->
          <td class="text-sm text-gray-900">
            {{ notification.created_at | date:'dd/MM/yyyy - HH:mm' }}
          </td>

          <!-- Expire le -->
          <td class="text-sm text-gray-900">
            <span *ngIf="notification.expires_at; else noExpiry">
              {{ notification.expires_at | date:'dd/MM/yyyy - HH:mm' }}
            </span>
            <ng-template #noExpiry>
              <span class="text-gray-400">Jamais</span>
            </ng-template>
          </td>

          <!-- Actions -->
          <td>
            <div class="flex items-center gap-2">
              <p-button 
                icon="pi pi-eye"
                (onClick)="previewNotificationDialog(notification)"
                styleClass="p-button-outlined p-button-sm"
                pTooltip="Aperçu">
              </p-button>

              <p-button 
                *ngIf="notification.is_active"
                icon="pi pi-ban"
                (onClick)="deactivateNotification(notification)"
                styleClass="p-button-outlined p-button-danger p-button-sm"
                pTooltip="Désactiver">
              </p-button>

              <p-button 
                *ngIf="!notification.is_active"
                icon="pi pi-check"
                styleClass="p-button-outlined p-button-secondary p-button-sm"
                [disabled]="true"
                pTooltip="Déjà désactivée">
              </p-button>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="text-center py-8">
            <i class="pi pi-inbox text-4xl text-gray-300 mb-4"></i>
            <p class="text-gray-500">Aucune notification trouvée</p>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <!-- Dialog de création -->
  <p-dialog 
    [(visible)]="showCreateDialog" 
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '600px' }"
    header="Créer une nouvelle notification">
    
    <form [formGroup]="notificationForm" (ngSubmit)="onSubmitNotification()">
      <div class="space-y-6">
        <!-- Titre -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Titre <span class="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            pInputText 
            formControlName="title"
            placeholder="Titre de la notification"
            class="w-full text-[16px]"
            [class.ng-invalid]="isFieldInvalid('title')"
            [class.ng-dirty]="isFieldInvalid('title')">
          <small *ngIf="isFieldInvalid('title')" class="text-red-500">
            {{ getFieldError('title') }}
          </small>
        </div>

        <!-- Message -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Message <span class="text-red-500">*</span>
          </label>
          <textarea 
            pInputTextarea 
            formControlName="message"
            placeholder="Contenu de la notification"
            rows="4"
            class="w-full"
            [class.ng-invalid]="isFieldInvalid('message')"
            [class.ng-dirty]="isFieldInvalid('message')">
          </textarea>
          <small *ngIf="isFieldInvalid('message')" class="text-red-500">
            {{ getFieldError('message') }}
          </small>
        </div>

        <!-- Type et apparence -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <p-dropdown 
              [options]="notificationTypes"
              formControlName="notification_type"
              optionLabel="label"
              optionValue="value"
              placeholder="Choisir le type"
              styleClass="w-full"
              (onChange)="onNotificationTypeChange()">
            </p-dropdown>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Icône</label>
            <p-dropdown 
              [options]="iconOptions"
              formControlName="icon"
              optionLabel="label"
              optionValue="value"
              placeholder="Choisir l'icône"
              styleClass="w-full">
            </p-dropdown>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Couleur</label>
            <p-dropdown 
              [options]="colorOptions"
              formControlName="icon_color"
              optionLabel="label"
              optionValue="value"
              placeholder="Choisir la couleur"
              styleClass="w-full">
            </p-dropdown>
          </div>
        </div>

        <!-- Date d'expiration -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Date d'expiration (optionnel)
          </label>
          <p-calendar 
            formControlName="expires_at"
            dateFormat="dd/mm/yy"
            [showTime]="true"
            [showSeconds]="false"
            placeholder="Choisir une date d'expiration"
            styleClass="w-full">
          </p-calendar>
          <small class="text-gray-500">
            Si vide, la notification n'expirera jamais
          </small>
        </div>

        <!-- Aperçu -->
        <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 class="text-sm font-medium text-gray-700 mb-3">Aperçu :</h4>
          <div class="flex items-start p-3 bg-white rounded border-l-4"
               [ngClass]="'border-' + (notificationForm.get('icon_color')?.value || 'blue') + '-500'">
            <div class="flex-shrink-0 mr-3">
              <div class="w-8 h-8 rounded-full flex items-center justify-center"
                   [ngClass]="'bg-' + (notificationForm.get('icon_color')?.value || 'blue') + '-100'">
                <i [class]="'pi ' + (notificationForm.get('icon')?.value || 'pi-bell') + ' text-' + (notificationForm.get('icon_color')?.value || 'blue') + '-600'"></i>
              </div>
            </div>
            <div>
              <div class="font-medium text-gray-900">
                {{ notificationForm.get('title')?.value || 'Titre de la notification' }}
              </div>
              <div class="text-sm text-gray-600 mt-1">
                {{ notificationForm.get('message')?.value || 'Contenu de la notification...' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button 
          label="Annuler" 
          icon="pi pi-times"
          (onClick)="showCreateDialog = false"
          styleClass="p-button-text">
        </p-button>
        
        <p-button 
          label="Créer la notification"
          icon="pi pi-send"
          (onClick)="onSubmitNotification()"
          [loading]="isSubmitting()"
          [disabled]="notificationForm.invalid">
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Dialog d'aperçu -->
  <p-dialog 
    [(visible)]="showPreviewDialog" 
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '500px' }"
    [header]="'Aperçu - ' + previewNotification?.title">
    
    <div *ngIf="previewNotification" class="space-y-4">
      <!-- Aperçu visuel -->
      <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div class="flex items-start p-3 bg-white rounded border-l-4"
             [ngClass]="'border-' + previewNotification.icon_color + '-500'">
          <div class="flex-shrink-0 mr-3">
            <div class="w-10 h-10 rounded-full flex items-center justify-center"
                 [ngClass]="'bg-' + previewNotification.icon_color + '-100'">
              <i [class]="'pi ' + previewNotification.icon + ' text-' + previewNotification.icon_color + '-600'"></i>
            </div>
          </div>
          <div class="flex-1">
            <div class="font-medium text-gray-900">{{ previewNotification.title }}</div>
            <div class="text-sm text-gray-600 mt-1">{{ previewNotification.message }}</div>
            <div class="text-xs text-gray-400 mt-2">
              {{ previewNotification.created_at | date:'dd/MM/yyyy à HH:mm' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Détails -->
      <div class="space-y-3">
        <div class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Type :</span>
          <p-tag 
            [value]="getNotificationTypeLabel(previewNotification.notification_type)"
            [severity]="getNotificationTypeSeverity(previewNotification.notification_type)">
          </p-tag>
        </div>

        <div class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Statut :</span>
          <p-tag 
            [value]="previewNotification.is_active ? 'Active' : 'Inactive'"
            [severity]="previewNotification.is_active ? 'success' : 'danger'">
          </p-tag>
        </div>

        <div class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Utilisateurs notifiés :</span>
          <span class="text-sm text-gray-900">{{ previewNotification.total_users_notified || 0 }}</span>
        </div>

        <div class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Notifications lues :</span>
          <span class="text-sm text-gray-900">{{ previewNotification.users_read_count || 0 }}</span>
        </div>

        <div class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Taux de lecture :</span>
          <span class="text-sm text-gray-900">{{ getReadPercentage(previewNotification) }}%</span>
        </div>

        <div *ngIf="previewNotification.expires_at" class="flex justify-between">
          <span class="text-sm font-medium text-gray-600">Expire le :</span>
          <span class="text-sm text-gray-900">{{ previewNotification.expires_at | date:'dd/MM/yyyy à HH:mm' }}</span>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <p-button 
        label="Fermer" 
        icon="pi pi-times"
        (onClick)="showPreviewDialog = false"
        styleClass="p-button-text">
      </p-button>
    </ng-template>
  </p-dialog>

  <!-- Toast et dialog de confirmation -->
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>
</div>